import { Alert, Box, Button, Stack, Typography } from "@mui/material"
import { Comparison } from "routes/schedule/compare/lib/diff3"
import Iconify from "minimals-template/components/Iconify"
import { If } from "lib/@components/if"
import { ChangeItemEditor } from "routes/schedule/plugins/legislation-resolutions/change-item-editor"
import { ChangeItemFooter } from "routes/schedule/plugins/legislation-resolutions/change-item-footer"
import { CriticalityDropdown } from "routes/schedule/plugins/legislation-resolutions/criticality-dropdown"
import { ProjectBridgeRewriteEditor } from "./project-bridge-rewrite-editor"
import {
    getProjectBridgeCanonicalPropagationProposedText,
    getProjectBridgeProposedDisplayText,
    getProjectBridgeRewriteEmptyState,
    hasProjectBridgeCanonicalPropagationReview,
    hasProjectBridgeSharedRewriteEditWarning,
} from "./project-bridge-rewrite-card-presenters"

export function ProjectBridgeRewriteCardProposedPane({
    item,
    editing,
    onStartEdit,
    onCancelEdit,
    onSaveText,
    onDecisionChange,
    onCriticalityChange,
    onCanonicalPropagationAction,
}) {
    const comparisonChange = buildComparisonChange(item)
    const content = renderProposedContent()
    const rationale = renderRationale()
    return (
        <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2">Recommended rewrite</Typography>
                <If truthy={item?.hasRewrite}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CriticalityDropdown value={item?.criticality ?? null} onChange={handleCriticalityChange} />
                        <If truthy={!editing}>
                            <Button
                                size="small"
                                variant="text"
                                onClick={onStartEdit}
                                startIcon={<Iconify icon="mdi:pencil-outline" sx={{ fontSize: 14 }} />}
                            >
                                Edit
                            </Button>
                        </If>
                    </Stack>
                </If>
            </Stack>

            {content}
            {renderCanonicalPropagationReview()}
            {rationale}

            <If truthy={item?.hasRewrite}>
                <ChangeItemFooter change={comparisonChange} onDecisionChange={onDecisionChange} />
            </If>
        </Stack>
    )

    function handleCriticalityChange(criticality) {
        if (!item?.changeId) return
        onCriticalityChange?.(item.changeId, criticality)
    }

    function handleApplyLatestCanonical() {
        if (!item?.changeId) return
        onCanonicalPropagationAction?.(item.changeId, "apply_latest_canonical")
    }

    function handleKeepCurrentCanonical() {
        if (!item?.changeId) return
        onCanonicalPropagationAction?.(item.changeId, "keep_current")
    }

    function renderCanonicalPropagationReview() {
        if (!hasProjectBridgeCanonicalPropagationReview(item)) return null
        const canonicalComparisonChange = buildCanonicalPropagationComparisonChange(item)

        return (
            <Stack spacing={1}>
                <Alert
                    severity="warning"
                    action={
                        <Stack direction="row" spacing={1}>
                            <Button
                                size="small"
                                color="warning"
                                variant="contained"
                                onClick={handleApplyLatestCanonical}
                                startIcon={<Iconify icon="mdi:check" sx={{ fontSize: 14 }} />}
                            >
                                Apply
                            </Button>
                            <Button
                                size="small"
                                color="inherit"
                                variant="outlined"
                                onClick={handleKeepCurrentCanonical}
                                startIcon={<Iconify icon="mdi:close" sx={{ fontSize: 14 }} />}
                            >
                                Keep
                            </Button>
                        </Stack>
                    }
                >
                    A newer canonical version is available for this recommendation.
                </Alert>
                <Box
                    className="styled-bullets"
                    sx={{
                        minHeight: 72,
                        p: 2,
                        border: "1px solid",
                        borderColor: "warning.light",
                        borderRadius: 1.5,
                        fontSize: "0.875rem",
                    }}
                >
                    <Comparison
                        text={canonicalComparisonChange.newText}
                        original={canonicalComparisonChange.oldText}
                        other={canonicalComparisonChange.oldText}
                    />
                </Box>
            </Stack>
        )
    }

    function renderProposedContent() {
        if (!item?.hasRewrite) {
            return (
                <Box
                    sx={{
                        minHeight: 96,
                        p: 2,
                        border: "1px dashed",
                        borderColor: "divider",
                        borderRadius: 1.5,
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        {getProjectBridgeRewriteEmptyState(item)}
                    </Typography>
                </Box>
            )
        }

        if (editing) {
            if (hasProjectBridgeSharedRewriteEditWarning(item)) {
                return (
                    <ProjectBridgeRewriteEditor
                        initialText={getProjectBridgeProposedDisplayText(item)}
                        item={item}
                        onSave={onSaveText}
                        onCancel={onCancelEdit}
                    />
                )
            }

            return (
                <ChangeItemEditor
                    initialText={getProjectBridgeProposedDisplayText(item)}
                    onSave={onSaveText}
                    onCancel={onCancelEdit}
                />
            )
        }

        return (
            <Box
                className="styled-bullets"
                sx={{
                    minHeight: 96,
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1.5,
                    fontSize: "0.875rem",
                }}
            >
                <Comparison
                    text={comparisonChange.newText}
                    original={comparisonChange.oldText}
                    other={comparisonChange.oldText}
                />
            </Box>
        )
    }

    function renderRationale() {
        if (!(item?.rationale && item?.hasRewrite)) return null
        return <Alert severity="info">{item?.rationale}</Alert>
    }
}

function buildComparisonChange(item) {
    return {
        id: item?.changeId,
        decision: item?.decision ?? null,
        oldText: item?.displayCurrentText ?? item?.currentText ?? "",
        newText: getProjectBridgeProposedDisplayText(item),
    }
}

function buildCanonicalPropagationComparisonChange(item) {
    return {
        id: item?.changeId,
        oldText: getProjectBridgeProposedDisplayText(item),
        newText: getProjectBridgeCanonicalPropagationProposedText(item),
    }
}
